import $ from 'jquery';
import { Controller } from 'stimulus';
import { get, post, put, FetchRequest } from '@rails/request.js';
import '@/js/modules/jquery_modal_defaults';

export default class extends Controller {
  static values = {
    availableCredits: Number,
    creditPrice: Number,
    minimumCredits: Number,
    imageReady: Boolean,
    cardUrl: String,
  };

  static targets = [
    'creditsAmount',
    'creditsAmountInput',
    'creditsCallout',
    'creditsRemaining',
    'creditsUsdAmount',
    'enhanceButton',
    'enhanceImageErrorFlash',
    'enhancedImageId',
    'flash',
    'hint',
    'enhancementForm',
    'originalImage',
    'purchaseCreditsAlertFlash',
    'purchaseCreditsSuccessFlash',
    'purchasedCreditsAmount',
    'submitButton',
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_enhance') === 'true') {
      params.delete('open_enhance');
      const cleanUrl = `${window.location.pathname}${params.size ? '?' + params : ''}`;
      window.history.replaceState({}, '', cleanUrl);
      this.showModal();
    }
  }

  async submitForm(event) {
    event.preventDefault();

    this.#showSection('enhanced-images__loading');

    const body = new FormData(this.enhancementFormTarget);
    const url = this.enhancementFormTarget.action;
    const response = await post(url, { body });

    if (response.ok) {
      if (this.#sidenav()) {
        this.#hideFlash();
      } else {
        this.enhanceImageErrorFlashTarget.classList.add('hide');
      }

      const result = await response.json;

      this.enhancedImageIdTarget.value = result.id;

      const enhancedImageUrl = result.url;
      const newImage = document.getElementById('enhanced-images__new-image');
      newImage.dataset.enhanced = enhancedImageUrl;

      this.#updateRemainingCreditsCallout(result.available_credits);
      this.dispatch('rendered', { target: document });

      // smoothen transition: wait for image to fully load before showing result section
      newImage.onload = () => {
        this.#showSection('enhanced-images__result');
        newImage.onload = null; // Clean up
      };
      newImage.src = enhancedImageUrl;
    } else if (this.#sidenav()) {
      this.#showSection('enhanced-images__form');
      this.#showFlash(
        'Something went wrong while generating your render. Please try again.',
        'error'
      );
    } else {
      this.enhanceImageErrorFlashTarget.classList.remove('hide');
      this.#showSection('enhanced-images__form');
    }
  }

  // The board_show_v2 sidebar card listens for `enhanced-images:rendered` and swaps in its re-rendered self.
  async refreshCard() {
    if (!this.hasCardUrlValue) {
      return;
    }

    await get(this.cardUrlValue, { responseKind: 'turbo-stream' });
  }

  // board_show_v2's sidebar card stays on the page after a render (refreshCard), so its Enhance button would reopen
  // the modal on the last result: start over from the prompt form, unless a render is still on its way.
  restartFromCard(event) {
    const cardButton = event.target.closest(
      '.enhanced-images-card [data-enhanced-images-target="enhanceButton"]'
    );
    const loading = !this.element
      .querySelector('.enhanced-images__loading')
      .classList.contains('hide');
    if (!cardButton || loading) {
      return;
    }

    this.#showSection('enhanced-images__form');
  }

  showModal() {
    $('#enhanced-images').modal();
  }

  toggleSubmit() {
    if (!this.imageReadyValue) {
      return;
    }

    // The prompt is optional: the default photorealistic prompt is always sent.
    this.submitButtonTarget.disabled = this.availableCreditsValue < this.minimumCreditsValue;
  }

  toggleFeedbackSubmit() {
    const checkedOutput = document.querySelector('input[name="feedback_output"]:checked');
    const checkedFuture = document.querySelector('input[name="feedback_future"]:checked');
    const filledFeedback =
      document.querySelector('textarea[name="feedback_text"]').value.trim().length > 0;

    document.querySelector('.p-button--enhanced-images__share-feedback').disabled =
      !checkedOutput || !checkedFuture || !filledFeedback;
  }

  toggleImage(event) {
    const imageElement = document.getElementById('enhanced-images__new-image');
    imageElement.src = imageElement.dataset[event.target.dataset.imageType];
  }

  showPayment(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__payment');
  }

  retryPrompt(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__form');
  }

  openFeedbackSection(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__feedback');
  }

  openResultSection(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__result');
  }

  allow(event) {
    // The modal binds this action without the param: it has no enhance button to enable.
    const removeClasses = event.params.removeClasses?.split(' ') ?? [];
    if (this.hasEnhanceButtonTarget) {
      this.enhanceButtonTarget.classList.remove(...removeClasses);
    }

    this.imageReadyValue = true;

    if (this.hasSubmitButtonTarget) {
      this.toggleSubmit();
    }

    const { detail } = event;
    const { data } = detail;
    const { variants } = data;
    const newImage = document.getElementById('enhanced-images__new-image');
    newImage.dataset.original = variants['sharpen'];
  }

  async submitFeedback() {
    const feedbackOutput = document.querySelector('input[name="feedback_output"]:checked')?.value;
    const feedbackFuture = document.querySelector('input[name="feedback_future"]:checked')?.value;
    const feedbackText = document.getElementById('feedback_text').value;

    const enhancedImageId = this.enhancedImageIdTarget.value;
    const body = JSON.stringify({
      enhanced_image: {
        feedback_output: feedbackOutput,
        feedback_future: feedbackFuture,
        feedback_text: feedbackText,
      },
    });

    const response = await put(`/api/df/enhanced_images/${enhancedImageId}`, { body });

    if (response.ok) {
      this.#showSection('enhanced-images__feedback-completed');
    }
  }

  updatePurchaseCredits(event) {
    this.creditsAmountTarget.textContent = parseInt(event.currentTarget.value) || 0;
    this.creditsUsdAmountTargets.forEach((total) => {
      total.textContent = (event.currentTarget.value * this.creditPriceValue).toFixed(2);
    });
  }

  async purchaseCredits() {
    const creditPurchaseSection = this.element.querySelector('.enhanced-images__payment');
    const purchasedCredits = this.creditsAmountInputTarget.value;
    creditPurchaseSection.classList.add('enhanced-images__payment--loading');

    const body = JSON.stringify({
      amount: purchasedCredits,
    });

    const request = new FetchRequest('post', '/credit_purchases', { body });
    const response = await request.perform();

    if (response.ok) {
      const result = await response.json;
      this.availableCreditsValue = result.available_credits;
      this.#showSection('enhanced-images__form');
      this.#updateRemainingCreditsCallout(result.available_credits);
      this.#resetCreditPurchaseForm();
      if (this.#sidenav()) {
        this.#showFlash(`${purchasedCredits} AI credits purchased successfully.`, 'success');
      } else {
        this.purchaseCreditsAlertFlashTarget.classList.add('hide');
        this.purchasedCreditsAmountTarget.textContent = purchasedCredits;
        this.purchaseCreditsSuccessFlashTarget.classList.remove('hide');
      }
    } else if (this.#sidenav()) {
      this.#showFlash(
        'We encountered an unexpected error. Please try your purchase again.',
        'error'
      );
    } else {
      this.purchaseCreditsAlertFlashTarget.classList.remove('hide');
    }
    this.toggleSubmit();
    creditPurchaseSection.classList.remove('enhanced-images__payment--loading');
  }

  cancelPurchase(event) {
    event.preventDefault();
    this.#resetCreditPurchaseForm();
    this.#showSection('enhanced-images__form');
  }

  downloadImage(event) {
    event.preventDefault();

    const enhancedImageId = this.enhancedImageIdTarget.value;
    const imageUrl = `/api/df/enhanced_images/${enhancedImageId}`;

    window.open(imageUrl, '_blank');
  }

  toggleHistory(event) {
    event.preventDefault();
    this.#history.classList.toggle('enhanced-images-callout__history--active');
  }

  closeHistory(event) {
    if (event.type === 'click' && this.element.contains(event.target)) {
      return;
    }

    // The sidebar card renders the History panel only once the design has a render.
    this.#history?.classList.remove('enhanced-images-callout__history--active');
  }

  get #history() {
    return this.element.querySelector('.enhanced-images-callout__history');
  }

  #resetCreditPurchaseForm() {
    this.creditsAmountInputTarget.value = '';
    this.creditsAmountTarget.textContent = 0;
    this.creditsUsdAmountTargets.forEach((total) => {
      total.textContent = 0;
    });
  }

  #updateRemainingCreditsCallout(availableCredits) {
    this.creditsRemainingTargets.forEach((creditsRemaining) => {
      creditsRemaining.textContent = availableCredits;
    });
    this.creditsCalloutTarget.classList.remove(
      'credits-callout--no-credit',
      'credits-callout--low-credit'
    );
    if (availableCredits < this.creditsCalloutTarget.dataset.noCredits) {
      this.creditsCalloutTarget.classList.add('credits-callout--no-credit');
    } else if (availableCredits < this.creditsCalloutTarget.dataset.lowCredits) {
      this.creditsCalloutTarget.classList.add('credits-callout--low-credit');
    }
  }

  #showSection(sectionClass) {
    this.#hideFlashMessages();
    document.querySelectorAll('.enhanced-images__section').forEach((el) => {
      el.classList.add('hide');
    });
    document.querySelector(`.${sectionClass}`).classList.remove('hide');

    if (this.#sidenav()) {
      this.hintTarget.dispatchEvent(new CustomEvent('flash:show'));
    }
  }

  #hideFlashMessages() {
    if (this.#sidenav()) {
      this.#hideFlash();

      return;
    }

    this.purchaseCreditsAlertFlashTarget.classList.add('hide');
    this.purchaseCreditsSuccessFlashTarget.classList.add('hide');
  }

  #sidenav() {
    return document.body.hasAttribute('data-sidenav-layout');
  }

  #showFlash(message, level) {
    this.flashTarget.dispatchEvent(new CustomEvent('flash:show', { detail: { message, level } }));
  }

  #hideFlash() {
    this.flashTarget.dispatchEvent(new CustomEvent('flash:hide'));
  }
}
